import { useEffect, useState } from "react";
import { CircleUserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.jsx";
import { apiGet, apiPatch, apiPost, apiPostEmpty } from "../lib/api.js";
import { hasPermission, loadCurrentSession } from "../lib/session.js";

export function MembersPage() {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [invitedName, setInvitedName] = useState("Ajay Singh");
  const [invitedPhone, setInvitedPhone] = useState("9876543210");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [intendedRole, setIntendedRole] = useState("member");
  const [inviteUrl, setInviteUrl] = useState("");
  const [message, setMessage] = useState("");
  const [copiedUrl, setCopiedUrl] = useState("");
  const [session, setSession] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const canManageMembers = hasPermission(session, "members.manage");

  useEffect(() => {
    loadCurrentSession()
      .then(setSession)
      .catch((error) => setMessage(error.message))
      .finally(() => setSessionLoaded(true));
  }, []);

  function getFamilyId() {
    return localStorage.getItem("nyasa_family_id");
  }

  async function loadMembers() {
    const familyId = getFamilyId();
    if (!familyId) {
      setMessage("Create or select a Kul first.");
      return;
    }

    try {
      const response = await apiGet(`/members/family/${familyId}`);
      setMembers(response.data);
      setMessage(response.data.length ? "Loaded members." : "No members found.");
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function loadInvitations() {
    const familyId = getFamilyId();
    if (!familyId) {
      setMessage("Create or select a Kul first.");
      return;
    }

    try {
      const response = await apiGet(`/invitations/family/${familyId}`);
      setInvitations(response.data);
      setMessage(response.data.length ? "Loaded invitation history." : "No invitations yet.");
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function createInvitation(event) {
    event.preventDefault();
    const familyId = getFamilyId();
    setMessage("");
    setInviteUrl("");

    if (!familyId) {
      setMessage("Create or select a Kul first.");
      return;
    }

    try {
      const response = await apiPost("/invitations", {
        familyId,
        invitedName,
        invitedEmail: invitedEmail || undefined,
        invitedPhone: invitedPhone || undefined,
        intendedRole
      });
      const absoluteInviteUrl = `${window.location.origin}${response.data.inviteUrl}`;
      setInviteUrl(absoluteInviteUrl);
      setMessage("Invitation created.");
      await loadInvitations();
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function copyInvite(url) {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setMessage("Invite link copied.");
  }

  async function revokeInvite(invitationId) {
    try {
      await apiPostEmpty(`/invitations/${invitationId}/revoke`);
      setMessage("Invitation revoked.");
      await loadInvitations();
    } catch (error) {
      setMessage(error.message);
    }
  }

  function absoluteInviteUrl(invitation) {
    return invitation.inviteUrl ? `${window.location.origin}${invitation.inviteUrl}` : "";
  }

  async function updateMemberRole(memberId, role) {
    const familyId = getFamilyId();
    if (!familyId) {
      setMessage("Create or select a Kul first.");
      return;
    }

    try {
      await apiPatch(`/members/family/${familyId}/${memberId}/role`, { role });
      setMessage("Member role updated.");
      await loadMembers();
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function updateMemberStatus(memberId, status) {
    const familyId = getFamilyId();
    if (!familyId) {
      setMessage("Create or select a Kul first.");
      return;
    }

    try {
      await apiPatch(`/members/family/${familyId}/${memberId}/status`, { status });
      setMessage(status === "removed" ? "Duplicate entry hidden from Kul." : "Member status updated.");
      await loadMembers();
    } catch (error) {
      setMessage(error.message);
    }
  }

  function formatRole(role) {
    return role.replaceAll("_", " ");
  }

  function memberIcon(member) {
    if (member.gender === "male") return <span className="avatar-letter">M</span>;
    if (member.gender === "female") return <span className="avatar-letter">F</span>;
    return <CircleUserRound size={20} />;
  }

  if (sessionLoaded && !canManageMembers) {
    return (
      <section>
        <PageHeader
          eyebrow="Sadasya Directory"
          title="Sadasya"
          description="Owner and admin area for invitations, roles, and member status."
        />
        <section className="content-band">
          <h2>Sadasya is for Kul admins</h2>
          <p>
            General Sadasya can see the Kul, complete their Parichay, follow Sankalp, and contribute through Kosh. Invites, role changes,
            deactivation, and removals stay with the owner/admin team.
          </p>
          <div className="button-row">
            <Link className="secondary-button" to="/family">
              Open Kul
            </Link>
            <Link className="secondary-button" to="/profile">
              Complete Parichay
            </Link>
            <Link className="secondary-button" to="/projects">
              View Sankalp
            </Link>
          </div>
          {message ? <p className="form-message">{message}</p> : null}
        </section>
      </section>
    );
  }

  return (
    <section>
      <PageHeader
        eyebrow="Sadasya Directory"
        title="Sadasya"
        description="Parichay, relationships, professions, cities, skills, and contribution history."
      />
      <section className="content-band">
        <h2>Invite Sadasya</h2>
        <form className="form-grid" onSubmit={createInvitation}>
          <label>
            Name
            <input value={invitedName} onChange={(event) => setInvitedName(event.target.value)} />
          </label>
          <label>
            Phone
            <input value={invitedPhone} onChange={(event) => setInvitedPhone(event.target.value)} type="tel" />
          </label>
          <label>
            Email optional
            <input value={invitedEmail} onChange={(event) => setInvitedEmail(event.target.value)} type="email" />
          </label>
          <label>
            Role
            <select value={intendedRole} onChange={(event) => setIntendedRole(event.target.value)}>
              <option value="admin">Admin</option>
              <option value="kosh_pramukh">Kosh Pramukh</option>
              <option value="project_lead">Sankalp Lead</option>
              <option value="member">Sadasya</option>
              <option value="viewer">Viewer</option>
              <option value="external_advisor">External Advisor</option>
            </select>
          </label>
          <button type="submit">Create Invite</button>
          <button type="button" className="secondary-button" onClick={loadMembers}>
            Load Sadasya
          </button>
          <button type="button" className="secondary-button" onClick={loadInvitations}>
            Load Invites
          </button>
        </form>
        {message ? <p className="form-message">{message}</p> : null}
        {inviteUrl ? (
          <div className="copy-box">
            <span>{inviteUrl}</span>
            <button type="button" onClick={() => copyInvite(inviteUrl)}>
              {copiedUrl === inviteUrl ? "Copied" : "Copy"}
            </button>
          </div>
        ) : null}
      </section>
      <section className="content-band spaced-band">
        <h2>Sadasya</h2>
        {members.length ? (
          <div className="list-stack">
            {members.map((member) => (
              <div className="member-row" key={member._id}>
                <div className="member-summary">
                  <span className={`member-avatar ${member.gender || "unknown"}`}>{memberIcon(member)}</span>
                  <div>
                    <strong>{member.displayName}</strong>
                    <span>
                      {formatRole(member.role)} - {member.status} - {member.livingStatus || "living"}
                    </span>
                    <span>{member.placeOfResidence || member.city || member.profession || "Parichay details pending"}</span>
                  </div>
                </div>
                <div className="row-actions">
                  <select value={member.role} onChange={(event) => updateMemberRole(member._id, event.target.value)}>
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                    <option value="kosh_pramukh">Kosh Pramukh</option>
                    <option value="project_lead">Sankalp Lead</option>
                    <option value="member">Sadasya</option>
                    <option value="viewer">Viewer</option>
                    <option value="external_advisor">External Advisor</option>
                  </select>
                  {member.status === "active" ? (
                    <button type="button" className="secondary-button" onClick={() => updateMemberStatus(member._id, "inactive")}>
                      Deactivate Login
                    </button>
                  ) : (
                    <button type="button" className="secondary-button" onClick={() => updateMemberStatus(member._id, "active")}>
                      Activate
                    </button>
                  )}
                  <button type="button" className="secondary-button" onClick={() => updateMemberStatus(member._id, "removed")}>
                    Mark Duplicate
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>Load Sadasya after creating or selecting a Kul.</p>
        )}
      </section>
      <section className="content-band spaced-band">
        <h2>Invitation History</h2>
        {invitations.length ? (
          <div className="list-stack">
            {invitations.map((invitation) => {
              const url = absoluteInviteUrl(invitation);
              return (
                <div className="invite-row" key={invitation.id}>
                  <div>
                    <strong>{invitation.invitedName || invitation.invitedPhone || invitation.invitedEmail}</strong>
                    <span>{formatRole(invitation.intendedRole)} - {invitation.status}</span>
                    <span>{invitation.invitedPhone || invitation.invitedEmail || "No contact saved"}</span>
                  </div>
                  <div className="row-actions">
                    {url ? (
                      <button type="button" className="secondary-button" onClick={() => copyInvite(url)}>
                        {copiedUrl === url ? "Copied" : "Copy"}
                      </button>
                    ) : null}
                    {invitation.status === "pending" ? (
                      <button type="button" className="secondary-button" onClick={() => revokeInvite(invitation.id)}>
                        Revoke
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p>Load invitations to see pending, accepted, and revoked invites.</p>
        )}
      </section>
    </section>
  );
}
